/**
 * noga-2025-catalog.ts - indexes + pure resolvers over the authoritative NOGA 2025 catalog
 * (packages/shared/src/noga-2025-catalog.data.ts), fully offline (no network, no external tool).
 *
 * This is the data backbone of the AI NOGA classifier (NOGA_CLASSIFIER seam): the two-stage
 * constrained selection narrows first to an Abteilung (2-digit division), then to a Klasse
 * (4-digit class) from ONLY the classes within that division. The model never free-generates a
 * code - it always picks an index from a candidate list built HERE from the official catalog, so
 * a hallucinated code is structurally impossible.
 *
 * `resolveNoga2025Label(code)` returns the official German Titel for any official NOGA 2025 code
 * (art/class/group/division/section), walking UP to the nearest labelled ancestor - so a stored
 * 6-digit Art still resolves even if only its 4-digit Klasse carries a distinct label. Deterministic
 * and pure.
 *
 * [Source: packages/shared/src/noga-2025-catalog.data.ts] (BFS Erlaeuterungen, retrieved 2026-08-24)
 * [Source: packages/shared/src/noga.ts] (resolveBranche - the legacy 5-digit + division resolver)
 */

import {
  NOGA_2025_CATALOG,
  type NogaCatalogEntry,
} from './noga-2025-catalog.data';

export {
  NOGA_2025_CATALOG,
  NOGA_2025_CATALOG_SOURCE,
  type NogaCatalogEntry,
  type NogaLevel,
} from './noga-2025-catalog.data';

// ── Indexes (built once at module load) ───────────────────────────────────────

/** Exact code -> entry (every level). */
export const NOGA_2025_BY_CODE: ReadonlyMap<string, NogaCatalogEntry> = new Map(
  NOGA_2025_CATALOG.map((e) => [e.code, e]),
);

/** All 87 Abteilungen (2-digit divisions), in official order - the stage-A candidate universe. */
export const NOGA_2025_DIVISIONS: readonly NogaCatalogEntry[] = NOGA_2025_CATALOG.filter(
  (e) => e.level === 'division',
);

/** All 651 Klassen (4-digit classes), in official order. */
export const NOGA_2025_CLASSES: readonly NogaCatalogEntry[] = NOGA_2025_CATALOG.filter(
  (e) => e.level === 'class',
);

/** All 21 Abschnitte (A-U sections). */
export const NOGA_2025_SECTIONS: readonly NogaCatalogEntry[] = NOGA_2025_CATALOG.filter(
  (e) => e.level === 'section',
);

/** classes grouped by their 2-digit division prefix (stage-B candidate lists). */
const CLASSES_BY_DIVISION: ReadonlyMap<string, readonly NogaCatalogEntry[]> = (() => {
  const m = new Map<string, NogaCatalogEntry[]>();
  for (const c of NOGA_2025_CLASSES) {
    const div = c.code.slice(0, 2);
    const bucket = m.get(div);
    if (bucket) bucket.push(c);
    else m.set(div, [c]);
  }
  return m;
})();

/** Strip everything but digits, tolerating dotted BFS notation ("62.10" -> "6210"). */
function toDigits(code: string): string {
  return code.replace(/[^0-9]/g, '');
}

/**
 * The 4-digit Klassen within an Abteilung (2-digit division), in official order.
 * The stage-B candidate list for the AI classifier. Empty for an unknown division.
 */
export function nogaClassesInDivision(divisionCode: string): readonly NogaCatalogEntry[] {
  const div = toDigits(divisionCode).slice(0, 2);
  return CLASSES_BY_DIVISION.get(div) ?? [];
}

/** True when `code` is an exact, official NOGA 2025 code at ANY level (section..art). */
export function isNoga2025Code(code: string | null | undefined): boolean {
  if (!code) return false;
  return NOGA_2025_BY_CODE.has(code.trim());
}

/** True when `code` is an official 4-digit NOGA 2025 Klasse. */
export function isNoga2025Class(code: string | null | undefined): boolean {
  if (!code) return false;
  const e = NOGA_2025_BY_CODE.get(code.trim());
  return e?.level === 'class';
}

/**
 * Official German Titel for a NOGA 2025 code. Tries an exact match first (art/class/group/
 * division/section), then walks UP the numeric hierarchy (6->4->3->2 digits) to the nearest
 * labelled ancestor. Returns `null` for nullish/empty input or a code with no valid ancestor.
 *
 * Deterministic and pure - never a guess. A letter (section) resolves directly.
 */
export function resolveNoga2025Label(code: string | null | undefined): string | null {
  if (!code) return null;
  const raw = code.trim();
  // Section letters (A-U) resolve directly.
  const exact = NOGA_2025_BY_CODE.get(raw);
  if (exact) return exact.label;

  const digits = toDigits(raw);
  if (digits.length < 2) return null;
  // Walk up: 6-digit art -> 4-digit class -> 3-digit group -> 2-digit division.
  for (const len of [6, 4, 3, 2]) {
    if (digits.length >= len) {
      const ancestor = NOGA_2025_BY_CODE.get(digits.slice(0, len));
      if (ancestor) return ancestor.label;
    }
  }
  return null;
}
